import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Dispatch } from "react";
import { Microservices } from "../../app/AllowedMicroservices";
import { RootState } from "../../app/store";
import apiActionCreator, { HttpMethods } from "../../services/apiActionCreator";
import { FetchStatus } from "../common/enums";

// Mirrors the response of GET /progress-audit/progress/:progressId/traces
// (service-learning, SuperAdmin only): the raw ProgressAudit documents
// for one Progress document, sorted oldest first.
export type ProgressAuditStatus = "started" | "completed" | "failed";
export type ProgressAuditBranch = "lesson" | "topic" | "course";
export type ProgressAuditExitPoint = "unauthorized" | "created" | "certificateError" | "saved" | "threw";

export interface IProgressAuditTraceFlags {
  progressItemExisted?: boolean;
  lessonCreated?: boolean;
  topicCreated?: boolean;
  lessonFinished?: boolean;
  topicFinished?: boolean;
  courseFinished?: boolean;
  topicFinishRejected?: boolean;
  courseFinishRejected?: boolean;
  evaluationAdded?: boolean;
  pointsAdded?: boolean;
  pointsError?: boolean;
  certificateCreated?: boolean;
  certificateError?: boolean;
  saved?: boolean;
}

export interface IProgressAuditTrace {
  _id: string;
  requestId: string;
  username: string;
  status: ProgressAuditStatus;
  receivedAt: string;
  finishedAt?: string;
  durationMs?: number;
  payload: {
    courseId?: string;
    lessonId?: string;
    topicId?: string;
    evaluationId?: string;
    progress?: number;
  };
  hasAuthorization: boolean;
  progressDocumentId: string;
  branch?: ProgressAuditBranch;
  exitPoint?: ProgressAuditExitPoint;
  flags?: IProgressAuditTraceFlags;
  before?: unknown;
  after?: unknown;
  error?: { message: string; stack?: string };
}

interface ProgressAuditTracesState {
  list: IProgressAuditTrace[];
  status: FetchStatus;
}

const initialState: ProgressAuditTracesState = {
  list: [],
  status: FetchStatus.Idle,
};

export const progressAuditTracesSlice = createSlice({
  name: "progressAuditTraces",
  initialState,
  reducers: {
    fetching: (state) => {
      state.status = FetchStatus.Fetching;
    },
    fetched: (state, action: PayloadAction<IProgressAuditTrace[]>) => {
      state.list = action.payload || [];
      state.status = FetchStatus.Fetched;
    },
    error: (state) => {
      state.list = [];
      state.status = FetchStatus.Error;
    },
    clear: (state) => {
      state.list = [];
      state.status = FetchStatus.Idle;
    },
  },
});

export const {
  fetching,
  fetched,
  error,
  clear: clearProgressAuditTraces,
} = progressAuditTracesSlice.actions;

export const fetchProgressAuditTracesByProgressId = (progressId: string) => (dispatch: Dispatch<object>) => {
  return dispatch(
    apiActionCreator({
      endpoint: `/progress-audit/progress/${progressId}/traces`,
      types: {
        requestType: fetching,
        successTypes: [
          {
            actionOrCreator: fetched,
          },
        ],
        errorTypes: [
          {
            actionOrCreator: error,
          },
        ],
      },
      method: HttpMethods.GET,
      microservice: Microservices.LEARNING_SERVICE,
      authenticated: true,
    })
  );
};

export const selectProgressAuditTraces = (state: RootState) => state.progressAuditTraces.list || [];
export const selectIsFetchingProgressAuditTraces = (state: RootState) =>
  state.progressAuditTraces.status === FetchStatus.Fetching;
export const selectHasProgressAuditTracesError = (state: RootState) =>
  state.progressAuditTraces.status === FetchStatus.Error;

export default progressAuditTracesSlice.reducer;
